import { Fragment, ReactNode, useEffect } from 'react'
import { Dialog, DialogBackdrop, DialogTitle, Transition, TransitionChild } from '@headlessui/react'
import clsx from 'clsx'
import { IconSvg } from 'components/common'

export interface BaseModalProps {
	open: boolean
	setOpen?: (open: boolean) => void
	title?: ReactNode
	children: ReactNode
	onClose?: () => void
	preventClose?: boolean
	hideCloseButton?: boolean
	hideHeader?: boolean
	size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | '3xl' | '4xl' | '5xl' | '6xl' | '7xl' | 'full'
	customSize?: string
	className?: string
	bodyClassName?: string
	headerClassName?: string
	backdropClassName?: string
	maxWidth?: string
	onOpened?: () => void
	onClosed?: () => void
	/**
	 * Renders a full-width action block above the modal content (e.g. a toolbar with gear + close).
	 * When provided, the default close button is suppressed.
	 * Receives `handleClose` so the block can close the modal through the full close sequence.
	 */
	customTopActionBlock?: (onClose: () => void) => ReactNode
}

type ModalSize = NonNullable<BaseModalProps['size']>

const sizeClasses: Record<ModalSize, string> = {
	sm: 'sm:max-w-sm',
	md: 'sm:max-w-md',
	lg: 'sm:max-w-lg',
	xl: 'sm:max-w-xl',
	'2xl': 'sm:max-w-2xl',
	'3xl': 'sm:max-w-3xl',
	'4xl': 'sm:max-w-4xl',
	'5xl': 'sm:max-w-5xl',
	'6xl': 'sm:max-w-6xl',
	'7xl': 'sm:max-w-7xl',
	full: 'sm:max-w-full',
}

export function BaseModal({
	open,
	setOpen,
	title,
	children,
	onClose,
	preventClose = false,
	hideCloseButton = false,
	hideHeader = false,
	size = 'md',
	customSize,
	className,
	bodyClassName,
	headerClassName,
	backdropClassName,
	maxWidth,
	onOpened,
	onClosed,
	customTopActionBlock,
}: Readonly<BaseModalProps>) {
	const handleClose = () => {
		onClose?.()
		setOpen?.(false)
	}

	useEffect(() => {
		if (open) {
			onOpened?.()
		} else {
			onClosed?.()
		}
	}, [open, onOpened, onClosed])

	return (
		<Transition show={open} as={Fragment}>
			<Dialog
				as="div"
				className="fixed z-10 inset-0 overflow-y-auto"
				onClose={preventClose ? () => {} : handleClose}
			>
				<div className="flex items-stretch justify-center h-[100dvh] min-h-[100dvh] p-0 text-center sm:items-end sm:block sm:h-auto sm:min-h-screen">
					<TransitionChild
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0"
						enterTo="opacity-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100"
						leaveTo="opacity-0"
					>
						<DialogBackdrop
							className={clsx('fixed inset-0 bg-black-75 transition-opacity', backdropClassName)}
							onClick={preventClose ? undefined : handleClose}
						/>
					</TransitionChild>

					{/* This element is to trick the browser into centering the modal contents. */}
					<span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
						&#8203;
					</span>

					<TransitionChild
						as={Fragment}
						enter="ease-out duration-300"
						enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
						enterTo="opacity-100 translate-y-0 sm:scale-100"
						leave="ease-in duration-200"
						leaveFrom="opacity-100 translate-y-0 sm:scale-100"
						leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
					>
						<div
							className={clsx(
								'relative block align-bottom overflow-hidden shadow-xl transform transition-all h-full w-full sm:inline-block sm:h-auto sm:my-8 sm:align-middle sm:min-w-0',
								!customSize && sizeClasses[size],
								customSize
							)}
							style={maxWidth ? { maxWidth } : undefined}
						>
							{!customTopActionBlock && !hideCloseButton && (
								<div className="absolute top-6 right-4 z-[1]">
									<button
										onClick={handleClose}
										className="w-10 h-10 rounded-full flex justify-center items-center bg-tint-surface shadow-button backdrop-blur-lg hover:bg-tint-plus-surface disabled:bg-tint-surface disabled:text-secondary-text disabled:shadow-none touch-manipulation"
									>
										<IconSvg icon="close" className="w-6 h-6 text-primary-icon" />
									</button>
								</div>
							)}

							<div
								className={clsx(
									'modal-content h-full px-8 pt-[max(2rem,env(safe-area-inset-top))] pb-[max(2rem,env(safe-area-inset-bottom))] sm:p-8 bg-primary-surface/[0.4] backdrop-blur-lg rounded-none sm:rounded-2xl flex flex-col',
									className
								)}
							>
								{!hideHeader && title && (
									<div
										className={clsx(
											'modal-header font-medium text-xl leading-6 mb-8 text-primary-text w-[70%] mx-auto',
											headerClassName
										)}
									>
										<DialogTitle as="h3">{title}</DialogTitle>
									</div>
								)}
								<div
									className={clsx(
										'modal-body flex-1 overflow-y-auto sm:h-fit sm:max-h-[85vh]',
										bodyClassName
									)}
								>
									{children}
								</div>
							</div>
							{/*
							 * Rendered AFTER modal-content so it paints above the backdrop-blur stacking
							 * context that modal-content creates, making the toolbar and its buttons
							 * clickable without requiring an explicit z-index.
							 */}
							{customTopActionBlock && customTopActionBlock(handleClose)}
						</div>
					</TransitionChild>
				</div>
			</Dialog>
		</Transition>
	)
}
